import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { clientes as apiClientes } from "../lib/api";
import { getTodayLocal } from "../lib/formato";
import ClienteCard from "../componentes/clientes/ClienteCard";
import ClientModal from "../componentes/clientes/ClientModal";
import ConfirmDeleteModal from "../componentes/ConfirmDeleteModal";
import PaginationControls from "../componentes/PaginationControls";
import Aviso from "../componentes/Aviso";
import RegistrarMovimientoModal from "../componentes/movimientos/RegistrarMovimientoModal";
import { SearchIcon, UserPlusIcon, PlusIcon } from "../componentes/Iconos";

// En la pantalla del negocio entran 5 tarjetas por fila, así que 15
// llena tres filas exactas sin dejar huecos. El listado pasa de las 66
// páginas que tenía con 6 por página a 27.
const POR_PAGINA = 15;

// Listado de clientes con búsqueda y paginación.
// La página y el texto buscado viven en la URL (?page=2&q=maria), así que
// el enlace se puede compartir y el botón atrás del navegador funciona.
const ClientesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const pagina = Number(searchParams.get("page")) || 1;
  const busqueda = searchParams.get("q") || "";

  // Lo que se ve escrito en la caja. Se copia a la URL con un retraso,
  // para no consultar al servidor en cada tecla.
  const [textoBuscado, setTextoBuscado] = useState(busqueda);

  const [datos, setDatos] = useState({ items: [], totalPages: 0 });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);

  const [modalCliente, setModalCliente] = useState({ abierto: false, cliente: null });
  const [confirmarBorrado, setConfirmarBorrado] = useState({ abierto: false, cliente: null });
  const [registrando, setRegistrando] = useState(false);

  useEffect(() => {
    setTextoBuscado(busqueda);
  }, [busqueda]);

  useEffect(() => {
    if (textoBuscado === busqueda) return;

    const temporizador = setTimeout(() => {
      const params = {};
      if (textoBuscado) params.q = textoBuscado;
      // Al cambiar la búsqueda se vuelve a la primera página.
      setSearchParams(params, { replace: true });
    }, 300);

    return () => clearTimeout(temporizador);
  }, [textoBuscado, busqueda, setSearchParams]);

  const cargarClientes = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      setDatos(await apiClientes.listar({ pagina, limite: POR_PAGINA, busqueda }));
    } catch (err) {
      setError(err.message);
      setDatos({ items: [], totalPages: 0 });
    } finally {
      setCargando(false);
    }
  }, [pagina, busqueda]);

  useEffect(() => {
    cargarClientes();
  }, [cargarClientes]);

  const cambiarPagina = (nueva) => {
    const params = {};
    if (busqueda) params.q = busqueda;
    if (nueva > 1) params.page = nueva;
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const guardarCliente = async (cliente) => {
    if (cliente.id) {
      await apiClientes.actualizar(cliente);
      setModalCliente({ abierto: false, cliente: null });
      // Se recarga la misma página y búsqueda: no se pierde el contexto.
      await cargarClientes();
      return;
    }

    // Un cliente nuevo se crea para registrarle algo, así que se abre su
    // ficha de una vez en lugar de dejarlo perdido entre los otros 392.
    const creado = await apiClientes.crear(cliente);
    setModalCliente({ abierto: false, cliente: null });
    navigate(`/clientes/${creado.id}`);
  };

  const eliminarCliente = async () => {
    const cliente = confirmarBorrado.cliente;
    if (!cliente) return;
    try {
      await apiClientes.eliminar(cliente.id);
      // Si era el último de la página, se retrocede una.
      if (datos.items.length === 1 && pagina > 1) {
        cambiarPagina(pagina - 1);
      } else {
        await cargarClientes();
      }
      setAviso(`Se eliminó a ${cliente.nombre_completo}.`);
    } catch (err) {
      alert(err.message);
    } finally {
      setConfirmarBorrado({ abierto: false, cliente: null });
    }
  };

  return (
    <>
      {modalCliente.abierto && (
        <ClientModal
          onSave={guardarCliente}
          onClose={() => setModalCliente({ abierto: false, cliente: null })}
          clientToEdit={modalCliente.cliente}
        />
      )}

      {confirmarBorrado.abierto && (
        <ConfirmDeleteModal
          onConfirm={eliminarCliente}
          onCancel={() => setConfirmarBorrado({ abierto: false, cliente: null })}
          message={
            `¿Seguro que quieres eliminar a ${confirmarBorrado.cliente?.nombre_completo}? ` +
            "También se borrarán todas sus ventas y abonos, y no se puede deshacer."
          }
        />
      )}

      {registrando && (
        <RegistrarMovimientoModal
          fecha={getTodayLocal()}
          clientesDelDia={[]}
          onClose={() => setRegistrando(false)}
          onGuardado={() => {
            setRegistrando(false);
            setAviso("Movimiento registrado.");
            // El saldo del cliente cambió, así que se recarga la lista.
            cargarClientes();
          }}
        />
      )}

      <Aviso mensaje={aviso} onCerrar={() => setAviso(null)} />

      <div className="toolbar toolbar-clientes">
        <div className="search-bar">
          <SearchIcon />
          <input
            type="text"
            placeholder="Buscar por nombre o teléfono..."
            value={textoBuscado}
            onChange={(e) => setTextoBuscado(e.target.value)}
          />
        </div>

        <div className="toolbar-acciones">
          <button
            className="btn-secundario"
            onClick={() => setModalCliente({ abierto: true, cliente: null })}
          >
            <UserPlusIcon /> Agregar cliente
          </button>
          <button className="add-btn" onClick={() => setRegistrando(true)}>
            <PlusIcon /> Registrar movimiento
          </button>
        </div>
      </div>

      <div className="content-area">
        {cargando ? (
          <p>Cargando...</p>
        ) : error ? (
          <p className="error-message">{error}</p>
        ) : datos.items.length === 0 ? (
          <p className="lista-sin-resultados">
            {busqueda
              ? `No se encontró ningún cliente que coincida con «${busqueda}».`
              : "Todavía no hay clientes registrados."}
          </p>
        ) : (
          <>
            <div className="clientes-grid">
              {datos.items.map((cliente) => (
                <ClienteCard
                  key={cliente.id}
                  cliente={cliente}
                  onDelete={(c) => setConfirmarBorrado({ abierto: true, cliente: c })}
                  onEdit={(c) => setModalCliente({ abierto: true, cliente: c })}
                />
              ))}
            </div>
            <PaginationControls
              currentPage={pagina}
              totalPages={datos.totalPages}
              onPageChange={cambiarPagina}
            />
          </>
        )}
      </div>
    </>
  );
};

export default ClientesPage;
